import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import clsx from 'clsx';
import { useKlantenStore, type AccountPatch, type CustomerCore } from '../../stores/klanten-store';
import { BRAND_COLORS } from '../../lib/order-constants';

export interface KlantgegevensCardHandle {
  focusContact: () => void;
  focusAddress: () => void;
  focusBrands: () => void;
}

interface Props {
  customer: CustomerCore;
  /** Forceer 1-koloms layout (drawer / smalle viewport). */
  compact?: boolean;
}

type Section = 'contact' | 'address' | null;

export const KlantgegevensCard = forwardRef<KlantgegevensCardHandle, Props>(function KlantgegevensCard(
  { customer, compact = false },
  ref
) {
  const updateCustomerAccount = useKlantenStore((s) => s.updateCustomerAccount);
  const [editing, setEditing] = useState<Section>(null);
  const contactRef = useRef<HTMLDivElement | null>(null);
  const addressRef = useRef<HTMLDivElement | null>(null);
  const brandsRef = useRef<HTMLDivElement | null>(null);

  const scrollTo = useCallback((target: HTMLDivElement | null) => {
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      focusContact: () => {
        setEditing('contact');
        requestAnimationFrame(() => scrollTo(contactRef.current));
      },
      focusAddress: () => {
        setEditing('address');
        requestAnimationFrame(() => scrollTo(addressRef.current));
      },
      focusBrands: () => {
        requestAnimationFrame(() => scrollTo(brandsRef.current));
      },
    }),
    [scrollTo]
  );

  return (
    <div className="mt-4 space-y-3">
      <div className={clsx('grid gap-3', compact ? 'grid-cols-1' : 'grid-cols-1 lg:grid-cols-2')}>
        <ContactCard
          ref={contactRef}
          customer={customer}
          isEditing={editing === 'contact'}
          onStartEdit={() => setEditing('contact')}
          onCancel={() => setEditing(null)}
          onSave={async (patch) => {
            const res = await updateCustomerAccount(patch);
            if (res.ok) setEditing(null);
            return res;
          }}
        />
        <AddressCard
          ref={addressRef}
          customer={customer}
          isEditing={editing === 'address'}
          onStartEdit={() => setEditing('address')}
          onCancel={() => setEditing(null)}
          onSave={async (patch) => {
            const res = await updateCustomerAccount(patch);
            if (res.ok) setEditing(null);
            return res;
          }}
        />
      </div>
      <BrandsStrip ref={brandsRef} customer={customer} />
    </div>
  );
});

// ─── CARD SHELL ─────────────────────────────────────────────────

function CardShell({ title, action, children }: { title: string; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-3.5">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">{title}</h2>
        {action}
      </div>
      {children}
    </div>
  );
}

function EditToggle({ editing, onStart, onCancel }: { editing: boolean; onStart: () => void; onCancel: () => void }) {
  return editing ? (
    <button type="button" onClick={onCancel} className="text-xs text-slate-500 hover:text-slate-700">
      Annuleer
    </button>
  ) : (
    <button type="button" onClick={onStart} className="text-xs text-indigo-600 hover:underline">
      Wijzigen
    </button>
  );
}

function FormFooter({ saving, error, onSave, onCancel }: {
  saving: boolean;
  error: string | null;
  onSave: () => void;
  onCancel: () => void;
}) {
  return (
    <>
      {error && <p className="mt-2 text-xs text-amber-700">⚠ {error}</p>}
      <div className="flex items-center gap-2 mt-3">
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          className="text-xs font-medium px-3 py-1.5 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
        >
          {saving ? 'Opslaan…' : 'Opslaan'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="text-xs text-slate-600 hover:text-slate-900 disabled:opacity-50"
        >
          Annuleren
        </button>
        <span className="text-[10px] text-slate-400 ml-auto">⌘+Enter / Esc</span>
      </div>
    </>
  );
}

// ─── row helpers ────────────────────────────────────────────────

/** Display-mode regel: alleen value, geen label (matchend met OrderDetailView). */
function Line({ value, muted }: { value: string; muted?: boolean }) {
  if (!value.trim()) return null;
  return (
    <p className={clsx('text-sm break-words', muted ? 'text-slate-500' : 'text-slate-900')}>{value}</p>
  );
}

/** Edit-mode veld: label inline-klein, voor form-duidelijkheid. */
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[5.5rem_1fr] items-center gap-x-3 py-0.5">
      <label className="text-[11px] uppercase tracking-wide text-slate-400">{label}</label>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

function TextInput(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className={clsx(
        'w-full text-sm text-slate-900 border border-slate-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent',
        props.className
      )}
    />
  );
}

// ─── CONTACT CARD ───────────────────────────────────────────────

interface SectionProps {
  customer: CustomerCore;
  isEditing: boolean;
  onStartEdit: () => void;
  onCancel: () => void;
  onSave: (patch: AccountPatch) => Promise<{ ok: true } | { ok: false; error: string }>;
}

const ContactCard = forwardRef<HTMLDivElement, SectionProps>(function ContactCard(
  { customer, isEditing, onStartEdit, onCancel, onSave },
  ref
) {
  const isPerson = customer.isPersonAccount === true;
  const [draft, setDraft] = useState(() => pickContact(customer));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isEditing) {
      setDraft(pickContact(customer));
      setError(null);
    }
  }, [customer, isEditing]);

  const save = async () => {
    setSaving(true);
    setError(null);
    const patch = diffContact(customer, draft);
    if (Object.keys(patch).length === 0) {
      onCancel();
      setSaving(false);
      return;
    }
    const res = await onSave(patch);
    setSaving(false);
    if (!res.ok) setError(res.error);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      void save();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    }
  };

  return (
    <div ref={ref} onKeyDown={isEditing ? handleKeyDown : undefined}>
      <CardShell
        title="Contact"
        action={<EditToggle editing={isEditing} onStart={onStartEdit} onCancel={onCancel} />}
      >
        {isEditing ? (
          <div className="space-y-1.5">
            {isPerson ? (
              <>
                <Field label="Voornaam">
                  <TextInput autoFocus value={draft.firstName} onChange={(e) => setDraft({ ...draft, firstName: e.target.value })} />
                </Field>
                <Field label="Achternaam">
                  <TextInput value={draft.lastName} onChange={(e) => setDraft({ ...draft, lastName: e.target.value })} />
                </Field>
              </>
            ) : (
              <Field label="Bedrijf">
                <TextInput autoFocus value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
              </Field>
            )}
            <Field label="E-mail">
              <TextInput type="email" value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })} />
            </Field>
            <Field label="Telefoon">
              <TextInput type="tel" value={draft.phone} onChange={(e) => setDraft({ ...draft, phone: e.target.value })} />
            </Field>
            {isPerson && (
              <Field label="Mobiel">
                <TextInput type="tel" value={draft.mobile} onChange={(e) => setDraft({ ...draft, mobile: e.target.value })} />
              </Field>
            )}
            <FormFooter saving={saving} error={error} onSave={save} onCancel={onCancel} />
          </div>
        ) : (
          <div className="space-y-0.5 leading-snug">
            <Line value={isPerson ? `${customer.firstName} ${customer.lastName}`.trim() : customer.name} />
            <Line value={customer.email} muted />
            <Line value={customer.phone} muted />
            {isPerson && customer.mobile && <Line value={`${customer.mobile} (mobiel)`} muted />}
            {!customer.email && !customer.phone && (
              <p className="text-sm text-slate-400 italic">Geen contactgegevens</p>
            )}
          </div>
        )}
      </CardShell>
    </div>
  );
});

// ─── ADDRESS CARD ───────────────────────────────────────────────

const AddressCard = forwardRef<HTMLDivElement, SectionProps>(function AddressCard(
  { customer, isEditing, onStartEdit, onCancel, onSave },
  ref
) {
  const [draft, setDraft] = useState(() => pickAddress(customer));
  // In display: shipping shows separately alleen als het écht afwijkt van billing.
  // In edit: zelfde default; user kan via "Wijkt af" aparte velden tonen.
  const [shippingDiffers, setShippingDiffers] = useState(() => shippingActuallyDiffers(customer));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isEditing) {
      setDraft(pickAddress(customer));
      setShippingDiffers(shippingActuallyDiffers(customer));
      setError(null);
    }
  }, [customer, isEditing]);

  const save = async () => {
    setSaving(true);
    setError(null);
    // Als shipping niet afwijkt: stuur shipping = billing (geen leegmaak — SF flow
    // vult ze sowieso op gelijkwaardig). Behoudt consistente data voor route-planning.
    const effectiveDraft = shippingDiffers
      ? draft
      : {
          ...draft,
          shippingStreet: draft.street,
          shippingPostcode: draft.postcode,
          shippingCity: draft.city,
          shippingCountry: draft.country,
          shippingAddressToevoeging: draft.billingAddressToevoeging,
        };
    const patch = diffAddress(customer, effectiveDraft);
    if (Object.keys(patch).length === 0) {
      onCancel();
      setSaving(false);
      return;
    }
    const res = await onSave(patch);
    setSaving(false);
    if (!res.ok) setError(res.error);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      void save();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    }
  };

  const showShippingInDisplay = shippingActuallyDiffers(customer);

  return (
    <div ref={ref} onKeyDown={isEditing ? handleKeyDown : undefined}>
      <CardShell
        title="Adres"
        action={<EditToggle editing={isEditing} onStart={onStartEdit} onCancel={onCancel} />}
      >
        {isEditing ? (
          <div className="space-y-1.5">
            <Field label="Straat + nr.">
              <TextInput autoFocus value={draft.street} onChange={(e) => setDraft({ ...draft, street: e.target.value })} />
            </Field>
            <Field label="Postcode">
              <TextInput value={draft.postcode} onChange={(e) => setDraft({ ...draft, postcode: e.target.value })} />
            </Field>
            <Field label="Plaats">
              <TextInput value={draft.city} onChange={(e) => setDraft({ ...draft, city: e.target.value })} />
            </Field>
            <Field label="Land">
              <TextInput placeholder="Nederland" value={draft.country} onChange={(e) => setDraft({ ...draft, country: e.target.value })} />
            </Field>
            <Field label="Toevoeging">
              <TextInput placeholder="2-hoog, achterom, …" value={draft.billingAddressToevoeging} onChange={(e) => setDraft({ ...draft, billingAddressToevoeging: e.target.value })} />
            </Field>

            <div className="mt-2 pt-2 border-t border-slate-100">
              {!shippingDiffers ? (
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs text-slate-500">↳ Verzendadres gelijk aan factuuradres</span>
                  <button
                    type="button"
                    onClick={() => setShippingDiffers(true)}
                    className="text-xs text-indigo-600 hover:underline whitespace-nowrap"
                  >
                    Wijkt af →
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Verzendadres</span>
                    <button
                      type="button"
                      onClick={() => setShippingDiffers(false)}
                      className="text-xs text-slate-500 hover:underline"
                    >
                      Gelijk aan factuur
                    </button>
                  </div>
                  <Field label="Straat + nr.">
                    <TextInput value={draft.shippingStreet} onChange={(e) => setDraft({ ...draft, shippingStreet: e.target.value })} />
                  </Field>
                  <Field label="Postcode">
                    <TextInput value={draft.shippingPostcode} onChange={(e) => setDraft({ ...draft, shippingPostcode: e.target.value })} />
                  </Field>
                  <Field label="Plaats">
                    <TextInput value={draft.shippingCity} onChange={(e) => setDraft({ ...draft, shippingCity: e.target.value })} />
                  </Field>
                  <Field label="Land">
                    <TextInput placeholder="Nederland" value={draft.shippingCountry} onChange={(e) => setDraft({ ...draft, shippingCountry: e.target.value })} />
                  </Field>
                  <Field label="Toevoeging">
                    <TextInput value={draft.shippingAddressToevoeging} onChange={(e) => setDraft({ ...draft, shippingAddressToevoeging: e.target.value })} />
                  </Field>
                </>
              )}
            </div>

            <FormFooter saving={saving} error={error} onSave={save} onCancel={onCancel} />
          </div>
        ) : (
          <div className="space-y-0.5 leading-snug">
            <Line value={joinAddress(customer.street, customer.billingAddressToevoeging)} />
            <Line value={[customer.postcode, customer.city].filter(Boolean).join(' ')} />
            <Line value={customer.country || 'Nederland'} muted />
            {!customer.street && !customer.city && (
              <p className="text-sm text-slate-400 italic">Geen adres bekend</p>
            )}
            {showShippingInDisplay ? (
              <div className="mt-2 pt-2 border-t border-slate-100 space-y-0.5">
                <p className="text-[11px] uppercase tracking-wide text-slate-400">Verzendadres</p>
                <Line value={joinAddress(customer.shippingStreet, customer.shippingAddressToevoeging)} />
                <Line value={[customer.shippingPostcode, customer.shippingCity].filter(Boolean).join(' ')} />
                <Line value={customer.shippingCountry || 'Nederland'} muted />
              </div>
            ) : (
              <p className="mt-2 pt-2 border-t border-slate-100 text-xs text-slate-400">↳ Verzendadres gelijk aan factuuradres</p>
            )}
          </div>
        )}
      </CardShell>
    </div>
  );
});

// ─── BRANDS STRIP (read-only chips) ─────────────────────────────

const BrandsStrip = forwardRef<HTMLDivElement, { customer: CustomerCore }>(function BrandsStrip({ customer }, ref) {
  return (
    <div ref={ref} className="bg-white rounded-xl shadow-sm border border-slate-200 px-3.5 py-2.5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <h2 className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">Merken</h2>
          <div className="flex items-center gap-1 flex-wrap" title="Automatisch op basis van orders">
            <BrandChip label="Cinco" active={customer.brandFlags.cinco} colorClass={BRAND_COLORS.CINCO ?? ''} />
            <BrandChip label="Kleda" active={customer.brandFlags.kleda} colorClass={BRAND_COLORS.KLEDA ?? ''} />
            <BrandChip label="SofaSaver" active={customer.brandFlags.sofasaver} colorClass={BRAND_COLORS.SOFASAVER ?? ''} />
            <BrandChip label="Schoonhuisshop" active={customer.brandFlags.schoonhuisshop} colorClass={BRAND_COLORS.SCHOONHUISSHOP ?? ''} />
          </div>
        </div>
        {customer.mutatieBron && (
          <span className="text-[10px] text-slate-400 whitespace-nowrap">Laatste mutatie via {customer.mutatieBron}</span>
        )}
      </div>
    </div>
  );
});

function BrandChip({ label, active, colorClass }: { label: string; active: boolean; colorClass: string }) {
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[11px] font-medium',
        active ? colorClass : 'border border-slate-200 text-slate-400'
      )}
    >
      <span className={clsx('w-1.5 h-1.5 rounded-full', active ? 'bg-current opacity-70' : 'bg-slate-300')} />
      {label}
    </span>
  );
}

// ─── helpers ────────────────────────────────────────────────────

function pickContact(c: CustomerCore) {
  return {
    name: c.name,
    firstName: c.firstName,
    lastName: c.lastName,
    email: c.email,
    phone: c.phone,
    mobile: c.mobile,
  };
}

function pickAddress(c: CustomerCore) {
  return {
    street: c.street,
    postcode: c.postcode,
    city: c.city,
    country: c.country,
    billingAddressToevoeging: c.billingAddressToevoeging,
    shippingStreet: c.shippingStreet,
    shippingPostcode: c.shippingPostcode,
    shippingCity: c.shippingCity,
    shippingCountry: c.shippingCountry,
    shippingAddressToevoeging: c.shippingAddressToevoeging,
  };
}

function diffContact(c: CustomerCore, d: ReturnType<typeof pickContact>): AccountPatch {
  const out: AccountPatch = {};
  if (c.isPersonAccount) {
    if (d.firstName !== c.firstName) out.firstName = d.firstName;
    if (d.lastName !== c.lastName) out.lastName = d.lastName;
    if (d.mobile !== c.mobile) out.mobile = d.mobile;
  } else {
    if (d.name !== c.name) out.name = d.name;
  }
  if (d.email !== c.email) out.email = d.email;
  if (d.phone !== c.phone) out.phone = d.phone;
  return out;
}

function diffAddress(c: CustomerCore, d: ReturnType<typeof pickAddress>): AccountPatch {
  const out: AccountPatch = {};
  (['street', 'postcode', 'city', 'country', 'billingAddressToevoeging',
    'shippingStreet', 'shippingPostcode', 'shippingCity', 'shippingCountry', 'shippingAddressToevoeging'] as const)
    .forEach((k) => {
      if (d[k] !== c[k]) (out as Record<string, string>)[k] = d[k];
    });
  return out;
}

/**
 * SF auto-vult ShippingX = BillingX via flow. Daarom is "verzendadres bestaat"
 * géén goede check voor "wijkt af" — we vergelijken de waardes na trim.
 */
function shippingActuallyDiffers(c: CustomerCore): boolean {
  const norm = (s: string) => s.trim().toLowerCase();
  return (
    norm(c.shippingStreet) !== norm(c.street) ||
    norm(c.shippingPostcode) !== norm(c.postcode) ||
    norm(c.shippingCity) !== norm(c.city) ||
    norm(c.shippingCountry) !== norm(c.country) ||
    norm(c.shippingAddressToevoeging) !== norm(c.billingAddressToevoeging)
  );
}

function joinAddress(street: string, toevoeging: string): string {
  if (!street && !toevoeging) return '';
  if (!toevoeging) return street;
  if (!street) return toevoeging;
  return `${street} ${toevoeging}`;
}
